import type { ChipVariant } from '@cere/cere-design-system';

/**
 * Canonical run states used across CEF agent surfaces. Aligned with the
 * orchestrator's run state machine plus an explicit IDLE for jobs whose
 * `state` arrives empty/missing.
 */
export type RunStatus =
  | 'WORKING'
  | 'COMPLETED'
  | 'FAILED'
  | 'QUEUED'
  | 'INPUT_REQUIRED'
  | 'CANCELED'
  | 'IDLE';

export interface RunStatusChipProps {
  variant: ChipVariant;
  label: string;
}

/**
 * Closed union of orchestrator job/activity state values the chip knows how
 * to render. Accepts any string at runtime (unknown values render as
 * `WORKING`); the closed type gives callers autocomplete and an
 * exhaustiveness check.
 *
 * Mirrors the SDK's `JobState` (queued | throttled | processing | completed |
 * failed | cancelled | dead | active) and `ActivityState` (pending |
 * dispatched | running | completed | failed) unions.
 */
export type JobState =
  | 'queued'
  | 'throttled'
  | 'pending'
  | 'dispatched'
  | 'running'
  | 'working'
  | 'processing'
  | 'active'
  | 'completed'
  | 'failed'
  | 'dead'
  | 'cancelled'
  | 'canceled';

export function jobStateToRunStatus(state: JobState | (string & {})): RunStatus {
  // Empty/missing state — happens for jobs the orchestrator created but
  // hasn't dispatched yet. Render as "Idle" rather than blank or "Working".
  if (!state) return 'IDLE';
  // Normalize so server casing variations ("Completed", "PROCESSING") still
  // resolve — otherwise the switch falls through to WORKING and every chip
  // reads as "Working" regardless of the underlying state.
  switch (state.toLowerCase()) {
    case 'completed':
    case 'terminal':
      return 'COMPLETED';
    case 'failed':
    case 'dead':
      return 'FAILED';
    case 'cancelled':
    case 'canceled':
      return 'CANCELED';
    case 'queued':
    case 'throttled':
    case 'pending':
    case 'dispatched':
      return 'QUEUED';
    case 'running':
    case 'working':
    case 'processing':
    case 'active':
      return 'WORKING';
    default:
      return 'WORKING';
  }
}

/**
 * Render the SDK's raw `state` value as a human label, preserving the
 * orchestrator vocabulary (`active`, `processing`, `throttled`, ...) rather
 * than collapsing every working-ish state into "Working". The chip variant
 * still comes from {@link runStatusChipProps} via {@link jobStateToRunStatus},
 * so colours stay grouped while labels stay specific.
 */
export function jobStateLabel(state: JobState | (string & {})): string {
  if (!state) return 'Idle';
  switch (state.toLowerCase()) {
    case 'active':       return 'Active';
    case 'processing':   return 'Processing';
    case 'running':      return 'Running';
    case 'working':      return 'Working';
    case 'queued':       return 'Queued';
    case 'throttled':    return 'Throttled';
    case 'pending':      return 'Pending';
    case 'dispatched':   return 'Dispatched';
    case 'completed':
    case 'terminal':     return 'Completed';
    case 'failed':       return 'Failed';
    case 'dead':         return 'Dead';
    case 'cancelled':
    case 'canceled':     return 'Canceled';
    default:             return state;
  }
}

export type StateTone = 'green' | 'amber' | 'red' | 'grey';

/**
 * Traffic-light tone for a job/activity state: green done, amber in-flight,
 * red failed, grey inert. Coarser than {@link runStatusChipProps}'s DS variant
 * on purpose — the activity feed wants an at-a-glance colour, not the full
 * orchestrator vocabulary.
 */
export function jobStateTone(state: JobState | (string & {})): StateTone {
  switch (jobStateToRunStatus(state)) {
    case 'COMPLETED':      return 'green';
    case 'FAILED':         return 'red';
    case 'WORKING':
    case 'QUEUED':
    case 'INPUT_REQUIRED': return 'amber';
    case 'CANCELED':
    case 'IDLE':           return 'grey';
  }
}

/**
 * Map a `RunStatus` to a Chip prop bag for the DS `Chip` component.
 */
export function runStatusChipProps(status: RunStatus): RunStatusChipProps {
  switch (status) {
    case 'WORKING':
      return { variant: 'info', label: 'Working' };
    case 'COMPLETED':
      return { variant: 'success', label: 'Completed' };
    case 'FAILED':
      return { variant: 'error', label: 'Failed' };
    case 'QUEUED':
      return { variant: 'default', label: 'Queued' };
    case 'IDLE':
      return { variant: 'default', label: 'Idle' };
    case 'INPUT_REQUIRED':
      return { variant: 'warning', label: 'Input required' };
    case 'CANCELED':
      return { variant: 'default', label: 'Canceled' };
  }
}
